import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Collapse,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AccessTime,
  Badge,
  Business,
  CheckCircle,
  Description,
  Email,
  Error as ErrorIcon,
  ExpandLess,
  ExpandMore,
  HelpOutline,
  LocationOn,
  NavigateBefore,
  NavigateNext,
  Payments,
  Person,
  Phone,
  School,
  UploadFile,
  WorkOutline,
} from "@mui/icons-material";
import axios from "../Services/axiosConfig";

interface FormData {
  jobTitle: string;
  jobDescription: string;
  screeningQuestions: string[];
  deadline: string;
}

interface CandidateApplicationFormProps {
  formId: string;
  onSuccess: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const steps = [
  "Personal Information",
  "Professional Information",
  "Screening Questions",
  "Upload CV",
];

const CandidateApplicationForm: React.FC<CandidateApplicationFormProps> = ({
  formId,
  onSuccess,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<FormData | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [location, setLocation] = useState("");

  const [highestEducation, setHighestEducation] = useState("");
  const [currentOrganization, setCurrentOrganization] = useState("");
  const [currentDesignation, setCurrentDesignation] = useState("");
  const [totalExperience, setTotalExperience] = useState("");
  const [relevantExperience, setRelevantExperience] = useState("");
  const [keyExpertise, setKeyExpertise] = useState("");

  const [currentSalary, setCurrentSalary] = useState("");
  const [benefits, setBenefits] = useState("");
  const [expectedSalary, setExpectedSalary] = useState("");

  const [answers, setAnswers] = useState<string[]>([]);
  const [cv, setCv] = useState<File | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [fileError, setFileError] = useState("");
  const [fieldsTouched, setFieldsTouched] = useState<{ [key: string]: boolean }>({});

  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("info");

  const activeStepProgress = useMemo(() => {
    return ((activeStep + 1) / steps.length) * 100;
  }, [activeStep]);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const response = await axios.get(`jobs/forms/${formId}`);
        const data = response.data as FormData;

        const deadlineDate = new Date(data.deadline);
        const currentDate = new Date();

        setFormData(data);
        setAnswers(new Array(data.screeningQuestions.length).fill(""));

        if (currentDate > deadlineDate) {
          setDeadlinePassed(true);
        } else {
          updateTimeRemaining(deadlineDate);
          const timer = setInterval(() => updateTimeRemaining(deadlineDate), 60000);
          return () => clearInterval(timer);
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
        setSnackbarMessage("Failed to load the application form. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormData();
  }, [formId]);

  const updateTimeRemaining = (deadline: Date) => {
    const now = new Date();
    const diff = deadline.getTime() - now.getTime();

    if (diff <= 0) {
      setDeadlinePassed(true);
      setTimeRemaining("");
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      setTimeRemaining(
        `${days} day${days !== 1 ? "s" : ""} ${hours} hour${
          hours !== 1 ? "s" : ""
        } remaining`
      );
    } else if (hours > 0) {
      setTimeRemaining(
        `${hours} hour${hours !== 1 ? "s" : ""} ${minutes} minute${
          minutes !== 1 ? "s" : ""
        } remaining`
      );
    } else {
      setTimeRemaining(`${minutes} minute${minutes !== 1 ? "s" : ""} remaining`);
    }
  };

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case "name":
        return value.trim() === "" ? "Name is required" : "";

      case "email": {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? "Please enter a valid email address" : "";
      }

      case "phone": {
        const phoneRegex = /^\+?[0-9\s-()]{8,20}$/;
        return !phoneRegex.test(value) ? "Please enter a valid phone number" : "";
      }

      case "gender":
        return value === "" ? "Please select your gender" : "";

      case "location":
        return value.trim() === "" ? "Location is required" : "";

      case "highestEducation":
        return value.trim() === "" ? "Highest education is required" : "";

      case "currentOrganization":
        return value.trim() === "" ? "Current organization is required" : "";

      case "currentDesignation":
        return value.trim() === "" ? "Current designation is required" : "";

      case "totalExperience":
        return value.trim() === "" ? "Total experience is required" : "";

      case "cv":
        if (!value) return "Please upload your CV";
        if (!ALLOWED_FILE_TYPES.includes(value.type)) {
          return "Only PDF and DOCX files are accepted";
        }
        if (value.size > MAX_FILE_SIZE) {
          return "File size should not exceed 5MB";
        }
        return "";

      default:
        if (field.startsWith("answer-")) {
          return !value || value.trim() === ""
            ? "This question requires an answer"
            : "";
        }
        return "";
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setFieldsTouched((prev) => ({ ...prev, [field]: true }));

    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));

    switch (field) {
      case "name":
        setName(value);
        break;

      case "email":
        setEmail(value);
        break;

      case "phone":
        setPhone(value);
        break;

      case "gender":
        setGender(value);
        break;

      case "location":
        setLocation(value);
        break;

      case "highestEducation":
        setHighestEducation(value);
        break;

      case "currentOrganization":
        setCurrentOrganization(value);
        break;

      case "currentDesignation":
        setCurrentDesignation(value);
        break;

      case "totalExperience":
        setTotalExperience(value);
        break;

      case "cv":
        setCv(value);
        setFileError(error);
        break;

      default:
        if (field.startsWith("answer-")) {
          const index = parseInt(field.split("-")[1], 10);
          const newAnswers = [...answers];
          newAnswers[index] = value;
          setAnswers(newAnswers);
        }
        break;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    setFieldsTouched((prev) => ({ ...prev, cv: true }));

    if (!file) {
      setCv(null);
      setFileError("Please upload your CV");
      return;
    }

    const error = validateField("cv", file);

    if (error) {
      setFileError(error);
      setCv(null);
      return;
    }

    setFileError("");
    setCv(file);
  };

  const validateStep = (step: number): boolean => {
    let isValid = true;
    const newErrors: { [key: string]: string } = {};

    if (step === 0) {
      const fields = ["name", "email", "phone", "gender", "location"];
      const values = [name, email, phone, gender, location];

      fields.forEach((field, index) => {
        const error = validateField(field, values[index]);
        if (error) {
          isValid = false;
          newErrors[field] = error;
        }
      });
    }

    if (step === 1) {
      const fields = [
        "highestEducation",
        "currentOrganization",
        "currentDesignation",
        "totalExperience",
      ];

      const values = [
        highestEducation,
        currentOrganization,
        currentDesignation,
        totalExperience,
      ];

      fields.forEach((field, index) => {
        const error = validateField(field, values[index]);
        if (error) {
          isValid = false;
          newErrors[field] = error;
        }
      });
    }

    if (step === 2 && formData) {
      formData.screeningQuestions.forEach((_, index) => {
        const error = validateField(`answer-${index}`, answers[index]);
        if (error) {
          isValid = false;
          newErrors[`answer-${index}`] = error;
        }
      });
    }

    if (step === 3) {
      const error = validateField("cv", cv);
      if (error) {
        isValid = false;
        setFileError(error);
        newErrors.cv = error;
      }
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  const markCurrentStepTouched = () => {
    if (activeStep === 0) {
      setFieldsTouched((prev) => ({
        ...prev,
        name: true,
        email: true,
        phone: true,
        gender: true,
        location: true,
      }));
    }

    if (activeStep === 1) {
      setFieldsTouched((prev) => ({
        ...prev,
        highestEducation: true,
        currentOrganization: true,
        currentDesignation: true,
        totalExperience: true,
      }));
    }

    if (activeStep === 2) {
      const touched = { ...fieldsTouched };
      formData?.screeningQuestions.forEach((_, index) => {
        touched[`answer-${index}`] = true;
      });
      setFieldsTouched(touched);
    }

    if (activeStep === 3) {
      setFieldsTouched((prev) => ({
        ...prev,
        cv: true,
      }));
    }
  };

  const handleNext = () => {
    if (activeStep >= steps.length - 1) return;

    if (validateStep(activeStep)) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    markCurrentStepTouched();
    setSnackbarMessage("Please correct the highlighted fields before proceeding.");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const personalInfoValid = validateStep(0);
    const professionalInfoValid = validateStep(1);
    const questionsValid = validateStep(2);
    const cvValid = validateStep(3);

    if (!personalInfoValid || !professionalInfoValid || !questionsValid || !cvValid) {
      setSnackbarMessage("Please fill all required fields correctly.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setIsSubmitting(true);
    setProgress(0);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + Math.random() * 18;
        return next >= 90 ? 90 : next;
      });
    }, 500);

    const applicationData = new FormData();

    applicationData.append("name", name);
    applicationData.append("email", email);
    applicationData.append("phone", phone);
    applicationData.append("gender", gender);
    applicationData.append("location", location);
    applicationData.append("answers", JSON.stringify(answers));

    applicationData.append("highestEducation", highestEducation);
    applicationData.append("currentOrganization", currentOrganization);
    applicationData.append("currentDesignation", currentDesignation);
    applicationData.append("totalExperience", totalExperience);
    applicationData.append("relevantExperience", relevantExperience);
    applicationData.append("keyExpertise", keyExpertise);

    applicationData.append("currentSalary", currentSalary);
    applicationData.append("benefits", benefits);
    applicationData.append("expectedSalary", expectedSalary);

    if (cv) {
      applicationData.append("cv", cv);
    }

    try {
      await axios.post(`jobs/forms/${formId}/submit`, applicationData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setProgress(100);
      setSnackbarMessage("Application submitted successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setShowThankYou(true);
      onSuccess();
    } catch (error: any) {
      console.error("Error submitting application:", error);

      if (error.response?.status === 409) {
        setAlreadyApplied(true);
        setSnackbarMessage("You've already submitted an application for this job.");
      } else {
        setSnackbarMessage("Failed to submit application. Please try again.");
      }

      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      clearInterval(progressInterval);
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      backgroundColor: "#fff",
      transition: "0.2s ease",
      "& fieldset": {
        borderColor: "#d0d5dd",
      },
      "&:hover fieldset": {
        borderColor: "#98a2b3",
      },
      "&.Mui-focused fieldset": {
        borderWidth: 1.5,
      },
    },
    "& .MuiInputLabel-root": {
      fontWeight: 500,
    },
  };

  const sectionHeader = (
    icon: React.ReactNode,
    title: string,
    description: string
  ) => (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.12),
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 850, letterSpacing: "-0.01em" }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f4f6f8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 430,
            p: 4,
            borderRadius: 4,
            textAlign: "center",
            border: "1px solid",
            borderColor: "#e4e7ec",
            boxShadow: "0 18px 60px rgba(16, 24, 40, 0.06)",
          }}
        >
          <CircularProgress size={46} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 800 }}>
            Loading application form
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Please wait while we fetch the job details.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!formData) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6f8", py: 8, px: 2 }}>
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: 4,
              border: "1px solid",
              borderColor: alpha(theme.palette.error.main, 0.2),
              boxShadow: "0 18px 60px rgba(16, 24, 40, 0.06)",
            }}
          >
            <ErrorIcon color="error" sx={{ fontSize: 56 }} />
            <Typography variant="h5" color="error" sx={{ mt: 2, fontWeight: 850 }}>
              Form not found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              The application form may have been closed, deleted, or the link is incorrect.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (showThankYou || deadlinePassed || alreadyApplied) {
    const title = deadlinePassed
      ? "Application Closed"
      : alreadyApplied
        ? "Already Applied"
        : "Thank You!";

    const message = deadlinePassed
      ? "The application deadline has passed. Applications are no longer being accepted for this position."
      : alreadyApplied
        ? "Our records show that you have already submitted an application for this job."
        : "Your application has been submitted successfully. We will review it and contact you soon.";

    const iconColor = deadlinePassed
      ? "error.main"
      : alreadyApplied
        ? "info.main"
        : "success.main";

    const iconBg = deadlinePassed
      ? alpha(theme.palette.error.main, 0.08)
      : alreadyApplied
        ? alpha(theme.palette.info.main, 0.08)
        : alpha(theme.palette.success.main, 0.08);

    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6f8", py: 8, px: 2 }}>
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 5 },
              textAlign: "center",
              borderRadius: 4,
              border: "1px solid",
              borderColor: "#e4e7ec",
              bgcolor: "#fff",
              boxShadow: "0 18px 60px rgba(16, 24, 40, 0.06)",
            }}
          >
            <Box
              sx={{
                width: 78,
                height: 78,
                borderRadius: "50%",
                mx: "auto",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: iconBg,
                color: iconColor,
              }}
            >
              {deadlinePassed ? (
                <AccessTime sx={{ fontSize: 42 }} />
              ) : alreadyApplied ? (
                <ErrorIcon sx={{ fontSize: 42 }} />
              ) : (
                <CheckCircle sx={{ fontSize: 42 }} />
              )}
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
              {title}
            </Typography>

            <Typography variant="body1" color="text.secondary">
              {message}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  const shouldShowDescriptionToggle =
    formData.jobDescription && formData.jobDescription.length > 650;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f4f6f8",
        py: { xs: 2, sm: 4, md: 5 },
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            maxWidth: 1220,
            mx: "auto",
            borderRadius: { xs: 3, sm: 4 },
            border: "1px solid",
            borderColor: "#e4e7ec",
            bgcolor: "#fff",
            overflow: "hidden",
            boxShadow: "0 24px 80px rgba(16, 24, 40, 0.07)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: { xs: 2.5, sm: 4, md: 5 },
              py: { xs: 3, sm: 4.5 },
              background:
                "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
              borderBottom: "1px solid",
              borderColor: "#eaecf0",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "flex-start" }}
              spacing={2}
            >
              <Box sx={{ maxWidth: 820 }}>
                <Chip
                  label="Job Application"
                  size="small"
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    fontWeight: 800,
                    color: "primary.main",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.12),
                  }}
                />

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 950,
                    letterSpacing: "-0.045em",
                    lineHeight: 1.08,
                    fontSize: { xs: "2rem", sm: "2.65rem", md: "3.15rem" },
                    color: "#101828",
                  }}
                >
                  {formData.jobTitle}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<AccessTime />}
                  label={`Deadline: ${new Date(formData.deadline).toLocaleDateString()}`}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    bgcolor: "#fff",
                    borderColor: "#d0d5dd",
                    color: "#344054",
                  }}
                />

                {timeRemaining && (
                  <Chip
                    label={timeRemaining}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 800,
                      color: "primary.main",
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      border: "1px solid",
                      borderColor: alpha(theme.palette.primary.main, 0.12),
                    }}
                  />
                )}
              </Stack>
            </Stack>

            {/* Job Description */}
            <Box
              sx={{
                mt: 3,
                p: { xs: 2.25, sm: 3 },
                borderRadius: 3,
                bgcolor: "#fff",
                border: "1px solid",
                borderColor: "#e4e7ec",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 1.5 }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "#f2f4f7",
                      color: "#475467",
                    }}
                  >
                    <Description fontSize="small" />
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                    Job Description
                  </Typography>
                </Stack>

                {shouldShowDescriptionToggle && (
                  <Button
                    size="small"
                    onClick={() => setIsDescriptionExpanded((prev) => !prev)}
                    endIcon={isDescriptionExpanded ? <ExpandLess /> : <ExpandMore />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      borderRadius: 2,
                    }}
                  >
                    {isDescriptionExpanded ? "Show less" : "Read more"}
                  </Button>
                )}
              </Stack>

              <Collapse
                in={isDescriptionExpanded || !shouldShowDescriptionToggle}
                collapsedSize={shouldShowDescriptionToggle ? 155 : undefined}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "#344054",
                    lineHeight: 1.85,
                    fontSize: { xs: "0.96rem", sm: "1.02rem" },
                    whiteSpace: "pre-line",
                  }}
                >
                  {formData.jobDescription}
                </Typography>
              </Collapse>
            </Box>
          </Box>

          {/* Stepper */}
          <Box
            sx={{
              px: { xs: 2.5, sm: 4, md: 5 },
              py: { xs: 2.5, sm: 3 },
              borderBottom: "1px solid",
              borderColor: "#eaecf0",
              bgcolor: "#fff",
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 800, color: "#344054" }}
                >
                  Step {activeStep + 1} of {steps.length}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {steps[activeStep]}
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={activeStepProgress}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  bgcolor: "#eaecf0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                  },
                }}
              />

              {!isMobile && (
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  sx={{
                    mt: 1,
                    "& .MuiStepLabel-label": {
                      fontWeight: 700,
                      fontSize: "0.86rem",
                    },
                    "& .MuiStepIcon-root": {
                      color: "#d0d5dd",
                    },
                    "& .MuiStepIcon-root.Mui-active": {
                      color: "primary.main",
                    },
                    "& .MuiStepIcon-root.Mui-completed": {
                      color: "success.main",
                    },
                  }}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}
            </Stack>
          </Box>

          {/* Form */}
          <Box
            sx={{
              px: { xs: 2.5, sm: 4, md: 5 },
              py: { xs: 3, sm: 4 },
            }}
          >
            <form ref={formRef} onSubmit={handleSubmit}>
              {activeStep === 0 && (
                <Box>
                  {sectionHeader(
                    <Person fontSize="small" />,
                    "Personal Information",
                    "Enter your basic contact information."
                  )}

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Full Name"
                        fullWidth
                        value={name}
                        onChange={(e) => handleFieldChange("name", e.target.value)}
                        error={fieldsTouched.name && !!errors.name}
                        helperText={fieldsTouched.name && errors.name}
                        required
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email Address"
                        fullWidth
                        type="email"
                        value={email}
                        onChange={(e) => handleFieldChange("email", e.target.value)}
                        error={fieldsTouched.email && !!errors.email}
                        helperText={fieldsTouched.email && errors.email}
                        required
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Phone Number"
                        fullWidth
                        value={phone}
                        onChange={(e) => handleFieldChange("phone", e.target.value)}
                        error={fieldsTouched.phone && !!errors.phone}
                        helperText={fieldsTouched.phone && errors.phone}
                        required
                        placeholder="+92 300 1234567"
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        required
                        error={fieldsTouched.gender && !!errors.gender}
                        sx={fieldSx}
                      >
                        <InputLabel>Gender</InputLabel>
                        <Select
                          label="Gender"
                          value={gender}
                          onChange={(e) => handleFieldChange("gender", e.target.value)}
                        >
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                          <MenuItem value="Other">Other</MenuItem>
                        </Select>

                        {fieldsTouched.gender && errors.gender && (
                          <FormHelperText>{errors.gender}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Location"
                        fullWidth
                        value={location}
                        onChange={(e) => handleFieldChange("location", e.target.value)}
                        error={fieldsTouched.location && !!errors.location}
                        helperText={fieldsTouched.location && errors.location}
                        required
                        placeholder="City, Country"
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOn color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeStep === 1 && (
                <Box>
                  {sectionHeader(
                    <WorkOutline fontSize="small" />,
                    "Professional Information",
                    "Share your education, experience, and salary expectations."
                  )}

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Highest Education"
                        fullWidth
                        value={highestEducation}
                        onChange={(e) =>
                          handleFieldChange("highestEducation", e.target.value)
                        }
                        error={
                          fieldsTouched.highestEducation && !!errors.highestEducation
                        }
                        helperText={
                          fieldsTouched.highestEducation && errors.highestEducation
                        }
                        required
                        placeholder="Example: BS Computer Science"
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <School color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Current Organization"
                        fullWidth
                        value={currentOrganization}
                        onChange={(e) =>
                          handleFieldChange("currentOrganization", e.target.value)
                        }
                        error={
                          fieldsTouched.currentOrganization &&
                          !!errors.currentOrganization
                        }
                        helperText={
                          fieldsTouched.currentOrganization &&
                          errors.currentOrganization
                        }
                        required
                        placeholder="Example: ABC Software"
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Current Designation"
                        fullWidth
                        value={currentDesignation}
                        onChange={(e) =>
                          handleFieldChange("currentDesignation", e.target.value)
                        }
                        error={
                          fieldsTouched.currentDesignation &&
                          !!errors.currentDesignation
                        }
                        helperText={
                          fieldsTouched.currentDesignation &&
                          errors.currentDesignation
                        }
                        required
                        placeholder="Example: Software Engineer"
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Badge color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Total Experience"
                        fullWidth
                        value={totalExperience}
                        onChange={(e) =>
                          handleFieldChange("totalExperience", e.target.value)
                        }
                        error={
                          fieldsTouched.totalExperience && !!errors.totalExperience
                        }
                        helperText={
                          fieldsTouched.totalExperience && errors.totalExperience
                        }
                        required
                        placeholder="Example: 3 years"
                        sx={fieldSx}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Relevant Experience"
                        fullWidth
                        value={relevantExperience}
                        onChange={(e) => setRelevantExperience(e.target.value)}
                        placeholder="Example: 2 years in .NET development"
                        sx={fieldSx}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Key Expertise"
                        fullWidth
                        value={keyExpertise}
                        onChange={(e) => setKeyExpertise(e.target.value)}
                        placeholder="Example: .NET Core, SQL Server, React"
                        sx={fieldSx}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Current Salary"
                        fullWidth
                        value={currentSalary}
                        onChange={(e) => setCurrentSalary(e.target.value)}
                        placeholder="Example: 150,000"
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Payments color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Expected Salary"
                        fullWidth
                        value={expectedSalary}
                        onChange={(e) => setExpectedSalary(e.target.value)}
                        placeholder="Example: 200,000"
                        sx={fieldSx}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Payments color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Benefits"
                        fullWidth
                        value={benefits}
                        onChange={(e) => setBenefits(e.target.value)}
                        placeholder="Example: Medical, PF, Bonus, Fuel Allowance"
                        multiline
                        rows={3}
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeStep === 2 && (
                <Box>
                  {sectionHeader(
                    <Description fontSize="small" />,
                    "Screening Questions",
                    "Answer the questions carefully. These answers help with shortlisting."
                  )}

                  <Stack spacing={2.5}>
                    {formData.screeningQuestions.map((question, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          p: { xs: 2, sm: 2.5 },
                          borderRadius: 3,
                          border: "1px solid",
                          borderColor:
                            fieldsTouched[`answer-${index}`] &&
                            errors[`answer-${index}`]
                              ? "error.main"
                              : "#e4e7ec",
                          bgcolor: "#fff",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          sx={{ mb: 1.5 }}
                        >
                          <Box
                            sx={{
                              minWidth: 30,
                              height: 30,
                              borderRadius: "50%",
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 13,
                              fontWeight: 850,
                              border: "1px solid",
                              borderColor: alpha(theme.palette.primary.main, 0.12),
                            }}
                          >
                            {index + 1}
                          </Box>

                          <Typography sx={{ fontWeight: 750, flex: 1, color: "#344054" }}>
                            {question}
                          </Typography>

                          <Tooltip title="Maximum 500 characters">
                            <IconButton size="small">
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <TextField
                          fullWidth
                          value={answers[index]}
                          onChange={(e) =>
                            handleFieldChange(`answer-${index}`, e.target.value)
                          }
                          error={
                            fieldsTouched[`answer-${index}`] &&
                            !!errors[`answer-${index}`]
                          }
                          helperText={
                            fieldsTouched[`answer-${index}`] &&
                            errors[`answer-${index}`]
                              ? errors[`answer-${index}`]
                              : `${answers[index]?.length || 0}/500 characters`
                          }
                          required
                          multiline
                          rows={4}
                          inputProps={{ maxLength: 500 }}
                          placeholder="Type your answer here..."
                          sx={fieldSx}
                        />
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}

              {activeStep === 3 && (
                <Box>
                  {sectionHeader(
                    <UploadFile fontSize="small" />,
                    "Upload CV",
                    "Upload your latest CV in PDF or DOCX format."
                  )}

                  <Paper
                    elevation={0}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      p: { xs: 3, sm: 5 },
                      textAlign: "center",
                      borderRadius: 4,
                      border: "1.5px dashed",
                      borderColor: fileError
                        ? "error.main"
                        : cv
                          ? "success.main"
                          : "#cfd4dc",
                      bgcolor: fileError
                        ? alpha(theme.palette.error.main, 0.04)
                        : cv
                          ? alpha(theme.palette.success.main, 0.04)
                          : "#fcfcfd",
                      cursor: "pointer",
                      transition: "0.2s ease",
                      "&:hover": {
                        borderColor: fileError
                          ? "error.main"
                          : cv
                            ? "success.main"
                            : "primary.main",
                        bgcolor: fileError
                          ? alpha(theme.palette.error.main, 0.05)
                          : cv
                            ? alpha(theme.palette.success.main, 0.05)
                            : alpha(theme.palette.primary.main, 0.035),
                      },
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx"
                      hidden
                      onChange={handleFileChange}
                    />

                    <Box
                      sx={{
                        width: 68,
                        height: 68,
                        borderRadius: "50%",
                        mx: "auto",
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: fileError
                          ? "error.main"
                          : cv
                            ? "success.main"
                            : "primary.main",
                        bgcolor: fileError
                          ? alpha(theme.palette.error.main, 0.08)
                          : cv
                            ? alpha(theme.palette.success.main, 0.08)
                            : alpha(theme.palette.primary.main, 0.08),
                      }}
                    >
                      {cv ? (
                        <CheckCircle sx={{ fontSize: 38 }} />
                      ) : (
                        <UploadFile sx={{ fontSize: 38 }} />
                      )}
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 850 }}>
                      {cv ? "CV selected" : "Click to upload your CV"}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {cv
                        ? cv.name
                        : "Accepted formats: PDF, DOCX. Maximum file size: 5MB."}
                    </Typography>

                    {fileError && (
                      <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
                        {fileError}
                      </Typography>
                    )}

                    {cv && !fileError && (
                      <Chip
                        label="Ready to submit"
                        color="success"
                        icon={<CheckCircle />}
                        sx={{ mt: 2, fontWeight: 750 }}
                      />
                    )}
                  </Paper>

                  {isSubmitting && (
                    <Box sx={{ mt: 3 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 8,
                          borderRadius: 99,
                          bgcolor: "#eaecf0",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 99,
                          },
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 1 }}
                      >
                        {progress < 100
                          ? "Submitting your application..."
                          : "Application submitted."}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 4 }} />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<NavigateBefore />}
                  disabled={activeStep === 0 || isSubmitting}
                  sx={{
                    borderRadius: 2.5,
                    px: { xs: 2.5, sm: 3.5 },
                    py: 1.15,
                    textTransform: "none",
                    fontWeight: 750,
                    borderColor: "#d0d5dd",
                    color: "#344054",
                  }}
                >
                  Back
                </Button>

                {activeStep === steps.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 2.5,
                      px: { xs: 3, sm: 4 },
                      py: 1.15,
                      textTransform: "none",
                      fontWeight: 850,
                      boxShadow: "none",
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Submit Application"
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<NavigateNext />}
                    sx={{
                      borderRadius: 2.5,
                      px: { xs: 3, sm: 4 },
                      py: 1.15,
                      textTransform: "none",
                      fontWeight: 850,
                      boxShadow: "none",
                    }}
                  >
                    Continue
                  </Button>
                )}
              </Stack>
            </form>
          </Box>
        </Paper>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CandidateApplicationForm;